import { splitAndCapitalizeEachWord } from '@lib/textFormatters'

/** Validation messages from a rejected submit, keyed by the field's `param_key`. */
export type FieldErrors = Record<string, Array<string>>

/** Rails puts errors that belong to no single field under `base`. */
const FORM_LEVEL_KEY = 'base'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const toMessages = (value: unknown): Array<string> =>
  (Array.isArray(value) ? value : [value]).flatMap((item) => {
    if (typeof item === 'string') return item.trim() ? [item] : []
    // Nested shape: `{ file: 'is too large' }` — flatten to one readable line.
    if (isRecord(item)) return Object.entries(item).map(([key, message]) => `${key} ${String(message)}`)

    return []
  })

/**
 * The fetcher throws the parsed error body, so a 422 arrives as plain data. The field map comes
 * either at the root (`{ errors }`) or nested (`{ data: { errors } }`); other shapes yield `{}`.
 */
export const extractFieldErrors = (body: unknown): FieldErrors => {
  if (!isRecord(body)) return {}

  const errors = isRecord(body.data) && isRecord(body.data.errors) ? body.data.errors : body.errors

  if (!isRecord(errors)) return {}

  return Object.entries(errors).reduce<FieldErrors>((acc, [field, value]) => {
    const messages = toMessages(value)

    if (messages.length) acc[field] = messages

    return acc
  }, {})
}

type SplitFieldErrors = {
  /** `[param_key, message]` pairs the form can paint under their own input. */
  fields: Array<[string, string]>
  /** Messages with no input to attach to — shown once above the form buttons. */
  formMessages: Array<string>
}

/** Errors for keys the form never rendered have no input to sit under, so they go form-level. */
export const splitFieldErrors = (errors: FieldErrors, knownKeys: Array<string>): SplitFieldErrors => {
  const known = new Set(knownKeys.filter(Boolean))

  return Object.entries(errors).reduce<SplitFieldErrors>(
    (acc, [field, messages]) => {
      const message = messages.join(', ')

      if (known.has(field)) acc.fields.push([field, message])
      else
        acc.formMessages.push(field === FORM_LEVEL_KEY ? message : `${splitAndCapitalizeEachWord(field)}: ${message}`)

      return acc
    },
    { fields: [], formMessages: [] },
  )
}
